import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Coins, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StakeholderInviteAccept() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const isThanks = window.location.pathname.endsWith('/thanks');
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedStatus, setConfirmedStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/stakeholders/invite/${token}`);
        if (!cancelled) setInvite(r.data);
      } catch (e) { if (!cancelled) toast.error(e.response?.data?.detail || 'Invalid invitation'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Polling on the thanks page
  useEffect(() => {
    if (!isThanks) return;
    let active = true;
    const isFree = new URLSearchParams(window.location.search).get('free') === '1';
    const poll = async () => {
      try {
        // Free memberships are already activated on accept; just refresh invite info
        const r = isFree
          ? await axios.get(`${API}/stakeholders/invite/${token}`)
          : await axios.get(`${API}/stakeholders/invite/${token}/confirm`);
        if (!active) return;
        if (r.data.status === 'accepted') {
          setConfirmedStatus(r.data);
          return;
        }
        setTimeout(poll, 2500);
      } catch { setTimeout(poll, 4000); }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThanks, token]);

  const startCheckout = async () => {
    setSubmitting(true);
    try {
      if (invite?.free_membership) {
        // Free comp membership — skip Stripe entirely
        await axios.post(`${API}/stakeholders/invite/${token}/accept-free`, { accepted_terms: accepted });
        // Redirect to the thanks page to poll for status
        window.location.href = `/stakeholder/invite/${token}/thanks?free=1`;
      } else {
        const r = await axios.post(`${API}/stakeholders/invite/${token}/checkout`, { accepted_terms: accepted });
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
      setSubmitting(false);
    }
  };

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" /></div>;
  }

  if (isThanks) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-950 flex items-center justify-center p-6" data-testid="stakeholder-thanks">
        <Card className="max-w-xl w-full">
          <CardContent className="p-8 text-center space-y-4">
            {confirmedStatus ? (
              <>
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                <h1 className="text-2xl font-bold">You're in.</h1>
                <p className="text-muted-foreground">{confirmedStatus.membership_net_cents ? `${fmt(confirmedStatus.membership_net_cents)} has been credited to your Eventism Wallet.` : 'Your stakeholder account is active.'} Check your email for sign-in instructions.</p>
                <Button asChild className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="thanks-go-dashboard">
                  <a href="/stakeholder/dashboard">Go to dashboard</a>
                </Button>
              </>
            ) : (
              <>
                <Loader2 className="w-12 h-12 animate-spin text-fuchsia-400 mx-auto" />
                <h1 className="text-2xl font-bold">Activating your membership…</h1>
                <p className="text-muted-foreground">We're confirming payment with Stripe. This usually takes a few seconds.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite || invite.status === 'expired') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h1 className="text-xl font-bold">Invitation no longer valid</h1>
            <p className="text-sm text-muted-foreground">This link has expired or doesn't exist. Ask your inviter to send a fresh one.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invite.status === 'accepted') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h1 className="text-xl font-bold">Already a stakeholder</h1>
            <p className="text-sm text-muted-foreground">Your membership is already active.</p>
            <Button asChild><a href="/login">Sign in</a></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 p-6" data-testid="stakeholder-invite-page">
      <div className="max-w-3xl mx-auto pt-12">
        <div className="text-center mb-8">
          <Coins className="w-12 h-12 text-fuchsia-400 mx-auto mb-3" />
          <h1 className="text-4xl sm:text-5xl font-bold font-['Space_Grotesk']">You're invited.</h1>
          <p className="text-base text-slate-300 mt-3 max-w-xl mx-auto">
            Eventism Stakeholders is a private, invite-only program for financial backers who fund our hosted events and earn pro-rata profit-share on every settlement.
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            {invite.free_membership ? (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-5 text-center">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300">Comp membership</div>
                <div className="text-3xl font-bold mt-1 text-emerald-300">Free</div>
                <div className="text-xs text-muted-foreground mt-2">No payment required. Wallet starts empty — top up later when you're ready to deploy capital into an event.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/30 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-indigo-300">Membership</div>
                  <div className="text-2xl font-bold mt-1">{fmt(invite.membership_net_cents)}</div>
                  <div className="text-xs text-muted-foreground mt-1">credited to your wallet</div>
                </div>
                <div className="rounded-lg bg-slate-800/40 border border-border p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Processing fee</div>
                  <div className="text-2xl font-bold mt-1">{fmt(invite.processing_fee_cents)}</div>
                  <div className="text-xs text-muted-foreground mt-1">covers Stripe (incl. for transparency)</div>
                </div>
                <div className="rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/30 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-fuchsia-300">Total today</div>
                  <div className="text-2xl font-bold mt-1">{fmt(invite.membership_gross_cents)}</div>
                  <div className="text-xs text-muted-foreground mt-1">one-time, non-refundable</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-6 space-y-3">
            <h2 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-indigo-400" /> Stakeholder Membership Agreement</h2>
            <div className="text-xs text-muted-foreground leading-relaxed space-y-2 max-h-64 overflow-y-auto pr-2 border border-border rounded-md p-3 bg-slate-900/40">
              <p><strong>1. Risk of loss.</strong> Eventism Stakeholder positions ("NFT Tickets") are private profit-sharing interests in individual events. They are <strong>not</strong> public securities, are <strong>not</strong> insured by SIPC or FDIC, and may lose all of their value. Past performance of any prior Eventism event does not guarantee future returns.</p>
              <p><strong>2. Not a registered offering.</strong> Eventism is not a registered broker-dealer, investment adviser, or securities exchange. These positions are offered privately to invited members only and are not registered under the Securities Act of 1933.</p>
              <p><strong>3. Non-refundable.</strong> Any Stakeholder Membership fee charged at activation is non-refundable. Funds added to your Eventism Wallet remain available for deployment into NFT Ticket purchases or future withdrawal subject to Eventism's settlement procedures.</p>
              <p><strong>4. No liquidity guarantee.</strong> NFT Tickets are not freely tradable. Payouts occur 3–7 business days after the underlying event concludes, after vendor reconciliation and refund windows close.</p>
              <p><strong>5. Eligibility.</strong> You represent that you are at least 18 years of age and have the financial sophistication to evaluate the merits and risks of this arrangement.</p>
              <p>By checking the box and proceeding to payment, you accept these terms.</p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="accept-terms-checkbox" />
              <span className="text-sm">I have read and agree to the Stakeholder Membership Agreement.</span>
            </label>
          </CardContent>
        </Card>

        <Button
          size="lg"
          className="w-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 h-14 text-base"
          disabled={!accepted || submitting}
          onClick={startCheckout}
          data-testid="start-checkout-btn"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          {invite.free_membership
            ? 'Activate free membership'
            : `Pay ${fmt(invite.membership_gross_cents)} & activate membership`}
        </Button>
        <p className="text-[11px] text-center text-muted-foreground mt-3">{invite.free_membership ? 'No payment required.' : 'Secured by Stripe. No card details ever touch our servers.'}</p>
      </div>
    </div>
  );
}
